import React from 'react';
import { Tooltip } from 'antd';
import {
    CrownFilled, CheckCircleFilled,
    ArrowRightOutlined, ReadOutlined, SolutionOutlined,
} from '@ant-design/icons';

/* ── plan badge ───────────────────────────────────────────────── */
const planBadge = (plan) => {
    if (!plan || plan === 'free') return null;
    const isUltimate = plan === 'ultimate';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            background: isUltimate
                ? 'linear-gradient(135deg,#F59E0B,#FCD34D)'
                : 'linear-gradient(135deg,#AD1457,#D81B60)',
            borderRadius: '6px', padding: '3px 8px',
        }}>
            <CrownFilled style={{ color: isUltimate ? '#78350F' : 'white', fontSize: '10px' }} />
            <span style={{ color: isUltimate ? '#78350F' : 'white', fontSize: '0.65rem', fontWeight: 700 }}>
                {plan.toUpperCase()}
            </span>
        </span>
    );
};

/* ── component ────────────────────────────────────────────────── */
const SearchCard = ({ profile }) => {
    const imageUrl = profile.profile_picture
        ? `${process.env.REACT_APP_IMAGE_URL}/${profile.profile_picture}`
        : (profile.gender?.toLowerCase() === 'female'
            ? process.env.REACT_APP_FEMALE_FALLBACK_IMAGE
            : process.env.REACT_APP_MALE_FALLBACK_IMAGE);

    const location = [profile.city, profile.state].filter(Boolean).join(', ');

    const handleCardClick = () => window.open(`/profile?id=${profile.profile_id}`, '_blank');

    return (
        <div
            onClick={handleCardClick}
            style={{
                background: 'white', borderRadius: '16px', overflow: 'hidden',
                border: '1px solid rgba(173,20,87,0.08)',
                transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                fontFamily: 'Calibri, sans-serif',
                display: 'flex', flexDirection: 'column', cursor: 'pointer',
                width: '100%', maxWidth: '320px', margin: '0 auto',
            }}
            onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 12px 48px rgba(173,20,87,0.2),0 4px 12px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = 'rgba(173,20,87,0.2)';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(173,20,87,0.08)';
            }}
        >
            {/* ── Photo ── */}
            <div style={{ position: 'relative', height: '220px', overflow: 'hidden', flexShrink: 0 }}>
                <img alt="profile" src={imageUrl} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.4s ease' }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />

                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%', background: 'linear-gradient(to top,rgba(26,26,46,0.78) 0%,transparent 100%)', pointerEvents: 'none' }} />

                {/* Name + chips */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{ color: 'white', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
                            {profile.first_name} {profile.last_name}
                        </span>
                        {profile.verified && (
                            <Tooltip title="Verified Profile">
                                <CheckCircleFilled style={{ color: '#34D399', fontSize: '14px' }} />
                            </Tooltip>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                        {profile.age && (
                            <span style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', borderRadius: '6px', padding: '2px 8px', color: 'rgba(255,255,255,0.9)', fontSize: '0.72rem', fontWeight: 500 }}>
                                {profile.age} yrs
                            </span>
                        )}
                        {profile.height && (
                            <span style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', borderRadius: '6px', padding: '2px 8px', color: 'rgba(255,255,255,0.9)', fontSize: '0.72rem', fontWeight: 500 }}>
                                {profile.height}
                            </span>
                        )}
                    </div>
                </div>

                {/* Plan badge — top right */}
                {profile.subscription_plan && profile.subscription_plan !== 'free' && (
                    <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                        {planBadge(profile.subscription_plan)}
                    </div>
                )}
            </div>

            {/* ── Body ── */}
            <div style={{ padding: '12px 14px', flex: 1 }}>
                {/* Caste / denomination tags */}
                {(profile.caste || profile.denomination) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                        {profile.caste && (
                            <span style={{ background: '#EDE9FE', color: '#AD1457', borderRadius: '6px', padding: '3px 10px', fontSize: '0.73rem', fontWeight: 600 }}>
                                {profile.caste}
                            </span>
                        )}
                        {profile.denomination && (
                            <span style={{ background: '#F0FDF4', color: '#059669', borderRadius: '6px', padding: '3px 10px', fontSize: '0.73rem', fontWeight: 600 }}>
                                {profile.denomination}
                            </span>
                        )}
                    </div>
                )}

                {/* Education & Occupation */}
                <div style={{ marginBottom: '8px' }}>
                    {profile.education && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <ReadOutlined style={{ color: '#94A3B8', fontSize: '0.82rem' }} />
                            <span style={{ color: '#475569', fontSize: '0.82rem', lineHeight: 1.3 }}>{profile.education}</span>
                        </div>
                    )}
                    {profile.occupation && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <SolutionOutlined style={{ color: '#94A3B8', fontSize: '0.82rem' }} />
                            <span style={{ color: '#475569', fontSize: '0.82rem', lineHeight: 1.3 }}>{profile.occupation}</span>
                        </div>
                    )}
                    {location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ color: '#94A3B8', fontSize: '0.82rem' }}>📍</span>
                            <span style={{ color: '#475569', fontSize: '0.82rem' }}>{location}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Footer CTA ── */}
            <div style={{ padding: '0 12px 12px' }}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    background: 'rgba(173,20,87,0.06)', borderRadius: '8px', padding: '8px 0',
                    color: '#AD1457', fontSize: '0.82rem', fontWeight: 600,
                }}>
                    View Full Profile <ArrowRightOutlined style={{ fontSize: '11px' }} />
                </div>
            </div>
        </div>
    );
};

export default SearchCard;
